<script>
   /****************************************************************
   * AliensPlot                                                    *
   * --------------------                                          *
   * shows plot with number of alien samples for different alpha   *
   *****************************************************************/

   import { qnorm } from 'mdatools/distributions';
   import { Vector } from 'mdatools/arrays';
   import { Axes, XAxis, YAxis, Box, Lines, Segments, Points } from 'svelte-plots-basic/2d';
   import { PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS } from '../../../shared/plots/Defaults.js';

   export let params;                                             // JSON with main plot parameters
   export let res;                                           // JSON with SIMCA results
   export let showGrid = true;                                    // logical, show grid or not
   export let xLabel = 'Number of wrongly accepted (expected)';   // label for x axis
   export let yLabel = 'Number of wrongly accepted (observed)';   // label for y axis
   export let ncomp = 1;                                          // number of components selected by user

   // plot saving parameters
   export let fileName = 'plot-aliens';
   export let pngWidth = PNG_WIDTH;
   export let pngHeight = PNG_HEIGHT;
   export let pngRes = PNG_RES;


   /*****************************************/
   /* Component code                        */
   /*****************************************/


   // local copies of Plot parameters
   let resID = res ? Object.keys(res)[0] : '';

   // function to compute observed extremes
   function getObserved(res) {
      const na = res.anum.aliens;
      const n = res.f.length;
      let observed = Vector.zeros(na);

      // loop over all samples but take into account only aliens
      for (let i = 0; i < n; i++) {
         if (res.a[i] !== 'alien') continue;
         observed = observed.add((fcrit.apply(v => res.f[i] < v)));
      }
      return observed;
   }


   $: if (params && params.resID !== resID) resID = params.resID;
   $: title = ncomp ? 'Aliens plot (A = ' + ncomp + ')' : '';
   $: resobj = res && resID ? res[resID] : null;
   $: ra = resobj ? resobj.v[ncomp - 1] : null;
   $: nObj = ra ? ra.anum.aliens : null;

   // compute expected values values
   let expected, Nm, Np, fcrit;
   $: if (nObj) {
      fcrit = Vector.zeros(nObj);
      expected = Vector.zeros(nObj);
      Nm = Vector.zeros(nObj);
      Np = Vector.zeros(nObj);

      for (let i = 1; i <= nObj; i++) {
         // compute expected beta
         const b = i / nObj;

         // based on beta compute critical value for f-distance
         const zb = qnorm(b);
         fcrit.v[i - 1] = ra.m * Math.pow(ra.Sz * zb + ra.Mz, 1.0 / ra.hz);

         // compute 95% interval for expected values
         const D = 2 * Math.sqrt(i * ( 1 - b));

         Nm.v[i - 1] = i - D;
         Np.v[i - 1] = i + D;
         expected.v[i - 1] = i;
      }

   } else {
      expected = null;
      Nm = null;
      Np = null;
      fcrit = null;
   }

   $: observed = ra ? getObserved(ra) : null;
</script>

{#if expected && observed }
<Axes {title} {xLabel} {yLabel}
   downloadLinks="hover"
   margins={MARGINS}
   limX={[-1, nObj + 2]}
   limY={[-1, nObj + 2]}
   {fileName} {pngWidth} {pngHeight} {pngRes}
>

   <Lines xValues={expected} yValues={expected} lineColor="#e0e0e0" />
   <Lines xValues={expected} yValues={Nm} lineColor="#e0e0e0" />
   <Lines xValues={expected} yValues={Np} lineColor="#e0e0e0" />
   <Segments xStart={expected} xEnd={expected} yStart={Nm} yEnd={Np}  lineColor="#e0e0e0"/>

   <Points xValues={expected} yValues={observed} borderWidth={2}/>

   <XAxis {showGrid} slot="xaxis" />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
{/if}